import React, { FC, Fragment, useCallback } from "react";
import { Container, createStyles, IconButton, makeStyles, Paper } from "@material-ui/core";
import { Colors } from "../../styles";
import {
    Change,
    ChangeNames,
    Configuration,
    DocumentState,
    LegalPerson,
    NaturalPerson,
    typeCadaster,
} from "../../clients/FirebaseClient/types";
import { defaultFirebaseClient } from "../../clients/FirebaseClient";
import { formatFileSize } from "../../utils/filetypes";
import { NamingRules, yesNoValue } from "./namingRules";
import { GroupType, ReportDefinition } from "./reportDefinition";
import { NavgateFunctionType, RenderFunctionOptions, screenIdByChange } from "../screens";
import moment from "moment";
import { useFormModel } from "../../components/core/FormModelHelpes";
import { LabledMultilineTextField } from "../../components/core/LabledMultilineTextField";
import { KWFormatter } from "../../components/forms/RemovalDateForm";
import { MONTHS } from "../../components/forms/RemovalReconstructionDateForm";
import { FormControlLabel } from "../../components/core/FormControlLabel";
import { Checkbox } from "../../components/core/Checkbox";
import { useTypedSelector } from "../../redux";
import { useStyles } from "./styles";
import { DownloadIcon } from "../../components/core/icons/Icons";
import DownloadSvgIcon from "../../assets/images/download_icon.svg";
import { sumUpChargingStationPower } from "../../utils";

const useComponentStyles = makeStyles(() =>
    createStyles({
        icon: {
            width: 30,
            height: 30,
            objectFit: "contain",
        },
        downloadLink: {
            color: Colors.mellowYellow,
            fontFamily: "innogyWEB",
            fontSize: 22,
            fontStretch: "normal",
            fontStyle: "normal",
            letterSpacing: "normal",
            lineHeight: 1.4,
        },
    }),
);

interface Props {
    configuration: Configuration;
    options?: RenderFunctionOptions;
    headerText?: string;
}

interface PropsSections {
    configuration: Configuration;
    navigate?: NavgateFunctionType;
}

interface PropsAddress {
    configuration: Configuration;
    group: GroupType;
    navigate?: NavgateFunctionType;
}

interface PropsAddressLine {
    label: string;
    value: string;
    valid: boolean;
    navigate?: NavgateFunctionType;
}

interface PropsSummaryGroup {
    configuration: Configuration;
    group: GroupType;
    navigate?: NavgateFunctionType;
}

interface PropsSection {
    title: string;
    children?: (false | JSX.Element)[];
}

interface PropsGroup {
    title: string;
    children: JSX.Element | JSX.Element[];
}

interface PropsSummaryTwoColumn {
    title: string;
    valid: boolean;
    value: string;
    changeName: ChangeNames;
    navigate?: NavgateFunctionType;
}

interface PropsSummaryThreeColumn {
    title: string;
    valid: boolean;
    valueOne: string;
    valueTwo: string;
    changeName: ChangeNames;
    navigate?: NavgateFunctionType;
}

interface PropsDocuments {
    configuration: Configuration;
    navigate?: NavgateFunctionType;
}

interface PropsDocument {
    file: DocumentState["files"][0];
}

/**
 * simple helper to clearify which checks are missing to reach exhautivness
 * @param param
 */
function exhaustiveCheck(param: never): void {
    if (param) return;
}

const Section: FC<PropsSection> = ({ title, children }: PropsSection) => {
    const classes = useStyles();

    return (
        <Paper className={classes.section}>
            <div className={classes.sectionTitleContainer}>
                <div className={classes.sectionTitle}>{title}</div>
            </div>
            {children}
        </Paper>
    );
};

const Group: FC<PropsGroup> = ({ title, children }: PropsGroup) => {
    const classes = useStyles();
    return (
        <div className={classes.groupContainer}>
            <div className={classes.groupTitle}>{title}</div>
            {children}
        </div>
    );
};

const SummaryTwoColumn: FC<PropsSummaryTwoColumn> = ({
    title,
    valid,
    value,
    changeName,
    navigate,
}: PropsSummaryTwoColumn) => {
    const classes = useStyles();
    const classesTitle = [classes.summaryTwoColumnTitle, valid ? null : classes.summaryError].join(
        " ",
    );
    const classesValue = [classes.summaryTwoColumnValue, valid ? null : classes.summaryError].join(
        " ",
    );
    const v = valid ? value : "Korrektur erforderlich";

    const onClick = (): void =>
        navigate && navigate(screenIdByChange(changeName) || "", changeName);

    return (
        <div
            className={classes.summaryTwoColumnContainer}
            key={changeName}
            onClick={onClick}
            style={{
                cursor: "pointer",
            }}
        >
            <div className={classesTitle}>{title}</div>
            <div className={classesValue}>{v}</div>
        </div>
    );
};

const SummaryThreeColumn: FC<PropsSummaryThreeColumn> = ({
    title,
    valid,
    valueOne,
    valueTwo,
    changeName,
    navigate,
}: PropsSummaryThreeColumn) => {
    const classes = useStyles();
    const classesTitle = [
        classes.summaryThreeColumnTitle,
        valid ? null : classes.summaryError,
    ].join(" ");
    const classesValueOne = [
        classes.summaryThreeColumnValueOne,
        valid ? null : classes.summaryError,
    ].join(" ");
    const classesValueTwo = [
        classes.summaryThreeColumnValueTwo,
        valid ? null : classes.summaryError,
    ].join(" ");

    const v1 = valid ? valueOne : "";
    const v2 = valid ? valueTwo : "Wert eintragen";

    const onClick = (): void =>
        navigate && navigate(screenIdByChange(changeName) || "", changeName);

    return (
        <div
            className={classes.summaryThreeColumnContainer}
            key={changeName}
            onClick={onClick}
            style={{
                cursor: "pointer",
            }}
        >
            <div className={classesTitle}>{title}</div>
            <div className={classesValueOne}>{v1}</div>
            <div className={classesValueTwo}>{v2}</div>
        </div>
    );
};

const Document: FC<PropsDocument> = ({ file }: PropsDocument) => {
    const classes = useStyles();
    return (
        <div className={classes.documentsContainer}>
            <div className={classes.documentsFile}>
                <IconButton
                    style={{
                        padding: 0,
                        marginRight: 8,
                        height: "30px",
                        color: Colors.mellowYellow,
                    }}
                    data-testid="download-button"
                    onClick={async () =>
                        (window.location.href = await defaultFirebaseClient.getDownloadUrl(
                            file.objectId,
                        ))
                    }
                >
                    <DownloadIcon />
                </IconButton>
                {file.userFilename} ({file.extension}, {formatFileSize(file.size)})
            </div>
            <div className={classes.documentsTimestamp}>
                {moment.unix(file.createdAt).format("DD.MM.Y, HH:mm U\\hr")}
            </div>
        </div>
    );
};

export const collectAllFilesArraysFromConfiguration = (
    configuration: Configuration,
): DocumentState["files"] => {
    const files = Object.keys(configuration.documents)
        .filter((classification) => {
            const doc = configuration.documents[classification];
            return doc.relevant && doc.progress === undefined;
        })
        .sort((class1, class2) => {
            const doc1 = configuration.documents[class1];
            const doc2 = configuration.documents[class2];

            if (!doc1.relevant || !doc2.relevant) return 0;

            return doc1.order - doc2.order;
        })
        .map((classification) => configuration.documents[classification].files);
    return files as DocumentState["files"];
};

const collectAllMissingNonOptionalFilesTitles = (configuration: Configuration): string[] => {
    const filesTitlesArray = Object.values(configuration.documents)
        .filter(
            (doc) =>
                doc.relevant &&
                !doc.optional &&
                (doc.progress !== undefined || doc.files.length < 1),
        )
        .map((doc) => doc.relevant && doc.title);
    return filesTitlesArray as string[];
};

const Documents: FC<PropsDocuments> = ({ configuration, navigate }: PropsDocuments) => {
    const classes = useStyles();
    const allFilesArrays = collectAllFilesArraysFromConfiguration(configuration);
    const missingNonOptionalFilesTitles = collectAllMissingNonOptionalFilesTitles(configuration);
    const allFiles = (allFilesArrays as []).reduce((acc, e) => acc.concat(e), []);

    return (
        <Fragment>
            {allFiles.length !== 0 && (
                <div className={classes.documentsHeaderContainer}>
                    <div className={classes.documentsHeaderFile}>Hochgeladene Datei</div>
                    <div className={classes.documentsHeaderTimestamp}>Uhrzeit</div>
                </div>
            )}
            {allFiles.map((file) => {
                const f = file as DocumentState["files"][0];
                return <Document file={f} key={moment.unix(f.createdAt).toString()} />;
            })}

            {missingNonOptionalFilesTitles.length !== 0 && (
                <Fragment>
                    {missingNonOptionalFilesTitles.length > 1 ? (
                        <div className={classes.missingDocumentsHeader}>
                            Folgende Pflichtdokumente fehlen:
                        </div>
                    ) : (
                        <div className={classes.missingDocumentsHeader}>
                            Folgendes Pflichtdokument fehlt:
                        </div>
                    )}
                    <div>
                        {missingNonOptionalFilesTitles.map((fileTitle, index) => (
                            <li
                                className={classes.missingDocumentsList}
                                key={index}
                                onClick={() => {
                                    navigate && navigate("documents");
                                }}
                                style={{
                                    cursor: "pointer",
                                }}
                            >
                                {fileTitle}
                            </li>
                        ))}
                    </div>
                </Fragment>
            )}
        </Fragment>
    );
};

const twoOrThreeColumn = (
    title: string,
    changeName: ChangeNames,
    configuration: Configuration,
    navigate?: (screenName: string) => void,
): JSX.Element => {
    const configurationState = configuration.state[
        changeName as keyof typeof configuration.state
    ] as Change;

    const namingRule = NamingRules.find(
        (m) => m.change === changeName || m.changes?.find((m) => m.change === changeName),
    );

    const formatedNumber = (value = 0, precision = 0): string => {
        return value.toLocaleString("de-DE", { minimumFractionDigits: precision });
    };

    switch (changeName) {
        case ChangeNames.PRODUCT_TYPES: {
            const change = configurationState as Change<typeof changeName>;
            return (
                <SummaryTwoColumn
                    key={changeName}
                    title={title}
                    value={
                        namingRule?.rules?.find(
                            (r) =>
                                r.condition.newConnection === change.state.value.newConnection ||
                                r.condition.modification === change.state.value.modification ||
                                r.condition.removal === change.state.value.removal ||
                                r.condition.charger === change.state.value.charger ||
                                r.condition.increase === change.state.value.increase ||
                                r.condition.adjustableLoad === change.state.value.adjustableLoad,
                        )?.name || "-"
                    }
                    valid={!change.default && !change.state.error}
                    changeName={changeName}
                    navigate={navigate}
                />
            );
        }
        case ChangeNames.NEW_CONNECTION_GAS_FEEDS: {
            const change = configurationState as Change<typeof changeName>;
            return (
                <SummaryTwoColumn
                    key={changeName}
                    title={title}
                    value={
                        namingRule?.rules?.find(
                            (r) =>
                                r.condition.interior === change.state.value.interior ||
                                r.condition.exterior === change.state.value.exterior,
                        )?.name || "-"
                    }
                    valid={!change.default && !change.state.error}
                    changeName={changeName}
                    navigate={navigate}
                />
            );
        }
        case ChangeNames.NEW_CONNECTION_BUILDING_INTAKES: {
            const change = configurationState as Change<typeof changeName>;
            return (
                <SummaryTwoColumn
                    key={changeName}
                    title={title}
                    value={
                        namingRule?.rules?.find(
                            (r) =>
                                r.condition.outerWall === change.state.value.outerWall ||
                                r.condition.basePlate === change.state.value.basePlate,
                        )?.name || "-"
                    }
                    valid={!change.default && !change.state.error}
                    changeName={changeName}
                    navigate={navigate}
                />
            );
        }
        case ChangeNames.NEW_CONNECTION_POWER_FEEDS: {
            const change = configurationState as Change<typeof changeName>;
            return (
                <SummaryTwoColumn
                    key={changeName}
                    title={title}
                    value={
                        namingRule?.rules?.find(
                            (r) =>
                                r.condition.interior === change.state.value.interior ||
                                r.condition.exterior === change.state.value.exterior ||
                                r.condition.overhead === change.state.value.overhead,
                        )?.name || "-"
                    }
                    valid={!change.default && !change.state.error}
                    changeName={changeName}
                    navigate={navigate}
                />
            );
        }
        case ChangeNames.NEW_CONNECTION_ROOF_TYPES: {
            const change = configurationState as Change<typeof changeName>;
            return (
                <SummaryTwoColumn
                    key={changeName}
                    title={title}
                    value={
                        namingRule?.rules?.find(
                            (r) =>
                                r.condition.flat === change.state.value.flat ||
                                r.condition.metal === change.state.value.metal ||
                                r.condition.other === change.state.value.other,
                        )?.name || "-"
                    }
                    valid={!change.default && !change.state.error}
                    changeName={changeName}
                    navigate={navigate}
                />
            );
        }
        case ChangeNames.PRODUCT_CLASSES: {
            const change = configurationState as Change<typeof changeName>;
            return (
                <SummaryTwoColumn
                    key={changeName}
                    title={title}
                    value={
                        change.state.value.power && change.state.value.gas
                            ? "Strom und Gas"
                            : change.state.value.gas
                            ? "Gas"
                            : change.state.value.power
                            ? "Strom"
                            : "-"
                    }
                    valid={!change.default && !change.state.error}
                    changeName={changeName}
                    navigate={navigate}
                />
            );
        }
        case ChangeNames.ADJUSTABLE_LOAD_TYPES: {
            const change = configurationState as Change<typeof changeName>;

            const labels = [];
            if (change.state.value.charger) {
                labels.push("Ladeeinrichtung");
            }
            if (change.state.value.heatPump) {
                labels.push("Wärmepumpe");
            }
            if (change.state.value.roomCoolingDevice) {
                labels.push("Gerät zur Raumkühlung");
            }

            const value =
                labels.length > 1
                    ? [labels.slice(0, -1).join(", "), labels.slice(-1)].join(" und ")
                    : labels[0] || "-";

            return (
                <SummaryTwoColumn
                    key={changeName}
                    title={title}
                    value={value}
                    valid={!change.default && !change.state.error}
                    changeName={changeName}
                    navigate={navigate}
                />
            );
        }
        case ChangeNames.NEW_CONNECTION_BUILDING_TYPES: {
            const change = configurationState as Change<typeof changeName>;
            return (
                <SummaryTwoColumn
                    key={changeName}
                    title={title}
                    value={
                        namingRule?.rules?.find(
                            (r) =>
                                r.condition.existing === change.state.value.existing ||
                                r.condition.new === change.state.value.new,
                        )?.name || "-"
                    }
                    valid={!change.default && !change.state.error}
                    changeName={changeName}
                    navigate={navigate}
                />
            );
        }
        case ChangeNames.NEW_CONNECTION_EXTERIOR_TYPES: {
            const change = configurationState as Change<typeof changeName>;
            return (
                <SummaryTwoColumn
                    key={changeName}
                    title={title}
                    value={
                        change.state.value.connectionPillarWithMeter
                            ? "Zähleranschlusssäule"
                            : change.state.value.connectionPillar
                            ? "Hausanschlusssäule"
                            : "-"
                    }
                    valid={!change.default && !change.state.error}
                    changeName={changeName}
                    navigate={navigate}
                />
            );
        }
        case ChangeNames.NEW_CONNECTION_CONNECTION_PILLAR_DELIVERED_BY_SYSTEM_OPERATOR: {
            const change = configurationState as Change<typeof changeName>;
            return (
                <SummaryTwoColumn
                    key={changeName}
                    title={title}
                    value={yesNoValue(change.state.value, "Von Syna", "Eigene Hausanschlusssäule")}
                    valid={!change.default && !change.state.error}
                    changeName={changeName}
                    navigate={navigate}
                />
            );
        }
        case ChangeNames.CHARGER_CONNECTION_EXTERIOR_TYPE: {
            const change = configurationState as Change<typeof changeName>;
            return (
                <SummaryTwoColumn
                    key={changeName}
                    title={title}
                    value={
                        change.state.value.connectionPillarWithMeter
                            ? "Zähleranschlusssäule"
                            : change.state.value.connectionPillar
                            ? "Hausanschlusssäule"
                            : change.state.value.chargerPillar
                            ? "Ladesäule"
                            : "-"
                    }
                    valid={!change.default && !change.state.error}
                    changeName={changeName}
                    navigate={navigate}
                />
            );
        }
        case ChangeNames.CHARGER_CONNECTION_PILLAR_DELIVERED_BY_SYSTEM_OPERATOR: {
            const change = configurationState as Change<typeof changeName>;
            return (
                <SummaryTwoColumn
                    key={changeName}
                    title={title}
                    value={yesNoValue(change.state.value, "Von Syna", "Eigene Ladeanschlusssäule")}
                    valid={!change.default && !change.state.error}
                    changeName={changeName}
                    navigate={navigate}
                />
            );
        }

        case ChangeNames.NEW_CONNECTION_CELLAR: {
            const change = configurationState as Change<typeof changeName>;
            return (
                <SummaryTwoColumn
                    key={changeName}
                    title={title}
                    value={yesNoValue(change.state.value, "mit Keller", "ohne Keller")}
                    valid={!change.default && !change.state.error}
                    changeName={changeName}
                    navigate={navigate}
                />
            );
        }
        case ChangeNames.NEW_CONNECTION_BUILDING_USAGES:
        case ChangeNames.REMOVAL_RECONSTRUCTION_BUILDING_USAGES:
        case ChangeNames.MODIFICATION_BUILDING_USAGES:
        case ChangeNames.INCREASE_BUILDING_USAGES:
        case ChangeNames.CHARGER_BUILDING_USAGE:
        case ChangeNames.ADJUSTABLE_LOAD_BUILDING_USAGE: {
            const change = configurationState as Change<typeof changeName>;
            return (
                <SummaryTwoColumn
                    key={changeName}
                    title={title}
                    value={
                        namingRule?.rules?.find(
                            (r) =>
                                r.condition.business === change.state.value.business &&
                                r.condition.household === change.state.value.household,
                        )?.name || "-"
                    }
                    valid={!change.default && !change.state.error}
                    changeName={changeName}
                    navigate={navigate}
                />
            );
        }

        case ChangeNames.ADJUSTABLE_LOAD_DATA_SHEET_DOWNLOAD: {
            const change = configurationState as Change<typeof changeName>;

            return (
                <Fragment>
                    {configuration.state.adjustableLoadTypes?.state.value.heatPump && (
                        <SummaryThreeColumn
                            key={changeName}
                            title={"Wärmepumpe"}
                            valueOne={"Ja"}
                            valueTwo={"(Gemäß Datenblatt)"}
                            valid={!change.default}
                            changeName={changeName}
                            navigate={navigate}
                        />
                    )}
                    {configuration.state.adjustableLoadTypes?.state.value.roomCoolingDevice && (
                        <SummaryThreeColumn
                            key={changeName}
                            title={"Gerät zur Raumkühlung"}
                            valueOne={"Ja"}
                            valueTwo={"(Gemäß Datenblatt)"}
                            valid={!change.default}
                            changeName={changeName}
                            navigate={navigate}
                        />
                    )}
                </Fragment>
            );
        }

        case ChangeNames.REMOVAL_BUILDING_OCCUPATION_CONFIRMATION: {
            const change = configurationState as Change<typeof changeName>;
            return (
                <SummaryTwoColumn
                    key={changeName}
                    title={title}
                    value={yesNoValue(change.state.value)}
                    valid={!change.default && !change.state.error}
                    changeName={changeName}
                    navigate={navigate}
                />
            );
        }
        case ChangeNames.REMOVAL_POWER_SUBJECT: {
            const change = configurationState as Change<typeof changeName>;
            return (
                <SummaryTwoColumn
                    key={changeName}
                    title={title}
                    value={
                        namingRule?.rules?.find(
                            (r) =>
                                r.condition.cableDisassembly ===
                                    change.state.value.cableDisassembly ||
                                r.condition.roofPillarDisassembly ===
                                    change.state.value.roofPillarDisassembly,
                        )?.name || "-"
                    }
                    valid={!change.default && !change.state.error}
                    changeName={changeName}
                    navigate={navigate}
                />
            );
        }
        case ChangeNames.REMOVAL_PERSONAL_CONTRIBUTION_CAN_DO_UNDERGROUND_WORK: {
            const change = configurationState as Change<typeof changeName>;
            return (
                <SummaryTwoColumn
                    key={changeName}
                    title={title}
                    value={change.state.value.yes ? "Ja" : "Nein"}
                    valid={true}
                    changeName={changeName}
                    navigate={navigate}
                />
            );
        }
        case ChangeNames.REMOVAL_RECONSTRUCTION: {
            const change = configurationState as Change<typeof changeName>;
            return (
                <SummaryTwoColumn
                    key={changeName}
                    title={title}
                    value={change.state.value.yes ? "Ja" : "Nein"}
                    valid={!change.default && !change.state.error}
                    changeName={changeName}
                    navigate={navigate}
                />
            );
        }
        case ChangeNames.REMOVAL_RECONSTRUCTION_PRODUCT_CLASSES: {
            const change = configurationState as Change<typeof changeName>;
            return (
                <SummaryTwoColumn
                    key={changeName}
                    title={title}
                    value={
                        namingRule?.rules?.find(
                            (r) =>
                                r.condition.power === change.state.value.power &&
                                r.condition.gas === change.state.value.gas,
                        )?.name || "-"
                    }
                    valid={!change.default && !change.state.error}
                    changeName={changeName}
                    navigate={navigate}
                />
            );
        }
        case ChangeNames.MODIFICATION_CELLAR: {
            const change = configurationState as Change<typeof changeName>;
            return (
                <SummaryTwoColumn
                    key={changeName}
                    title={title}
                    value={yesNoValue(change.state.value, "mit Keller", "ohne Keller")}
                    valid={!change.default && !change.state.error}
                    changeName={changeName}
                    navigate={navigate}
                />
            );
        }
        case ChangeNames.INCREASE_CURRENT_HOUSING_UNITS:
        case ChangeNames.INCREASE_EXPECTED_HOUSING_UNITS:
        case ChangeNames.ADJUSTABLE_LOAD_HOUSING_UNITS:
        case ChangeNames.CHARGER_HOUSING_UNITS:
        case ChangeNames.NEW_CONNECTION_HOUSING_UNITS:
        case ChangeNames.MODIFICATION_HOUSING_UNITS:
        case ChangeNames.REMOVAL_RECONSTRUCTION_HOUSING_UNITS: {
            const change = configurationState as Change<typeof changeName>;
            return (
                <SummaryTwoColumn
                    key={changeName}
                    title={title}
                    value={change.state.value.units?.toString() || ""}
                    valid={!change.default && !change.state.error}
                    changeName={changeName}
                    navigate={navigate}
                />
            );
        }
        case ChangeNames.NEW_CONNECTION_ELECTRIC_BUSINESS_DEMAND:
        case ChangeNames.ADJUSTABLE_LOAD_ELECTRICAL_BUSINESS_DEMAND:
        case ChangeNames.CHARGER_ELECTRICAL_BUSINESS_DEMAND:
        case ChangeNames.NEW_CONNECTION_GAS_DEMAND:
        case ChangeNames.MODIFICATION_POWER_CABLE_JUNCTION_BOX_RELOCATION_DISTANCE: {
            const change = configurationState as Change<typeof changeName>;
            return (
                <SummaryTwoColumn
                    key={changeName}
                    title={title}
                    value={
                        formatedNumber(
                            change.state.value.amount.num || 0,
                            change.state.value.precision,
                        ) +
                        " " +
                        change.state.value.unit
                    }
                    valid={!change.default && !change.state.error}
                    changeName={changeName}
                    navigate={navigate}
                />
            );
        }
        case ChangeNames.INCREASE_CURRENT_ELECTRIC_BUSINESS_DEMAND:
        case ChangeNames.INCREASE_CURRENT_GAS_DEMAND: {
            const change = configurationState as Change<typeof changeName>;
            return (
                <SummaryTwoColumn
                    key={changeName}
                    title={title}
                    value={
                        change.state.value.notKnown
                            ? "nicht bekannt"
                            : formatedNumber(
                                  change.state.value.amount.num || 0,
                                  change.state.value.precision,
                              ) +
                              " " +
                              change.state.value.unit
                    }
                    valid={!change.default && !change.state.error}
                    changeName={changeName}
                    navigate={navigate}
                />
            );
        }
        case ChangeNames.MODIFICATION_POWER_CABLE_RELOCATION_DISTANCE: {
            const change = configurationState as Change<typeof changeName>;
            return (
                <SummaryTwoColumn
                    key={changeName}
                    title={
                        configuration.state.modificationPowerCable?.state.value.cableRelocation
                            ? "Umlegung Netzanschlusskabel"
                            : "Hausanschlusskastens versetzen & Umlegung Netzanschlusskabel"
                    }
                    value={
                        formatedNumber(
                            change.state.value.amount.num || 0,
                            change.state.value.precision,
                        ) +
                        " " +
                        change.state.value.unit
                    }
                    valid={!change.default && !change.state.error}
                    changeName={changeName}
                    navigate={navigate}
                />
            );
        }
        case ChangeNames.MODIFICATION_PERSONAL_CONTRIBUTION_CAN_DO_UNDERGROUND_WORK: {
            const change = configurationState as Change<typeof changeName>;
            return (
                <SummaryTwoColumn
                    key={changeName}
                    title={title}
                    value={change.state.value.yes ? "Ja" : "Nein"}
                    valid={true}
                    changeName={changeName}
                    navigate={navigate}
                />
            );
        }
        case ChangeNames.MODIFICATION_PERSONAL_CONTRIBUTION_CAN_DO_WALL_BUSHING: {
            const change = configurationState as Change<typeof changeName>;
            return (
                <SummaryTwoColumn
                    key={changeName}
                    title={title}
                    value={change.state.value.yes ? "Ja" : "Nein"}
                    valid={true}
                    changeName={changeName}
                    navigate={navigate}
                />
            );
        }
        case ChangeNames.NEW_CONNECTION_ELECTRIC_BOILER:
        case ChangeNames.NEW_CONNECTION_ELECTRIC_HEAT_PUMP: {
            const change = configurationState as Change<typeof changeName>;
            return (
                <SummaryTwoColumn
                    key={changeName}
                    title={title}
                    value={change.state.value.yes ? "Ja" : "Nein"}
                    valid={!change.default && !change.state.error}
                    changeName={changeName}
                    navigate={navigate}
                />
            );
        }
        case ChangeNames.NEW_CONNECTION_ELECTRIC_HEAT_PUMP_V2:
        case ChangeNames.NEW_CONNECTION_ELECTRIC_ROOM_COOLING_DEVICE:
        case ChangeNames.NEW_CONNECTION_ELECTRIC_LOADER_V2:
        case ChangeNames.INCREASE_EXPECTED_ELECTRIC_ROOM_COOLING_DEVICE:
        case ChangeNames.INCREASE_EXPECTED_ELECTRIC_HEAT_PUMP_V2:
        case ChangeNames.INCREASE_EXPECTED_ELECTRIC_LOADER_V2: {
            const change = configurationState as Change<typeof changeName>;
            return (
                <SummaryTwoColumn
                    key={changeName}
                    title={title}
                    value={
                        change.state.value.yes
                            ? 'Ja, Anmeldung "Verbrauchsgeräte" erforderlich'
                            : "Nein"
                    }
                    valid={!change.default && !change.state.error}
                    changeName={changeName}
                    navigate={navigate}
                />
            );
        }
        case ChangeNames.NEW_CONNECTION_ELECTRIC_HEATING_SYSTEM: {
            const change = configurationState as Change<typeof changeName>;
            return (
                <SummaryTwoColumn
                    key={changeName}
                    title={title}
                    value={change.state.value.yes ? "Ja" : "Nein"}
                    valid={!change.default && !change.state.error}
                    changeName={changeName}
                    navigate={navigate}
                />
            );
        }
        case ChangeNames.NEW_CONNECTION_ELECTRIC_HEATING_SYSTEM_V2:
        case ChangeNames.NEW_CONNECTION_ELECTRIC_ELEVATOR:
        case ChangeNames.NEW_CONNECTION_ELECTRIC_INFEED:
        case ChangeNames.NEW_CONNECTION_ELECTRIC_LOADER:
        case ChangeNames.NEW_CONNECTION_ELECTRIC_SHARED_ELECTRICITY:
        case ChangeNames.NEW_CONNECTION_ELECTRIC_SAUNA:
        case ChangeNames.ADJUSTABLE_LOAD_ELECTRICAL_ELEVATOR:
        case ChangeNames.ADJUSTABLE_LOAD_ELECTRIC_LOADER:
        case ChangeNames.ADJUSTABLE_LOAD_ELECTRIC_SHARED_ELECTRICITY:
        case ChangeNames.ADJUSTABLE_LOAD_ELECTRIC_SAUNA:
        case ChangeNames.ADJUSTABLE_LOAD_ELECTRICAL_BOILER:
        case ChangeNames.ADJUSTABLE_LOAD_ELECTRICAL_HEAT_PUMP:
        case ChangeNames.CHARGER_ELECTRICAL_ELEVATOR:
        case ChangeNames.CHARGER_ELECTRIC_LOADER:
        case ChangeNames.CHARGER_ELECTRIC_SHARED_ELECTRICITY:
        case ChangeNames.CHARGER_ELECTRIC_SAUNA:
        case ChangeNames.CHARGER_ELECTRICAL_BOILER:
        case ChangeNames.CHARGER_ELECTRICAL_HEAT_PUMP:
        case ChangeNames.INCREASE_EXPECTED_ELECTRIC_BOILER:
        case ChangeNames.INCREASE_EXPECTED_ELECTRIC_BUSINESS_DEMAND:
        case ChangeNames.INCREASE_EXPECTED_GAS_DEMAND:
        case ChangeNames.INCREASE_EXPECTED_ELECTRIC_HEATING_SYSTEM:
        case ChangeNames.INCREASE_EXPECTED_ELECTRIC_HEAT_PUMP:
        case ChangeNames.INCREASE_EXPECTED_ELECTRIC_ELEVATOR:
        case ChangeNames.INCREASE_EXPECTED_ELECTRIC_LOADER:
        case ChangeNames.INCREASE_EXPECTED_ELECTRIC_SHARED_ELECTRICITY:
        case ChangeNames.INCREASE_EXPECTED_ELECTRIC_SAUNA: {
            const change = configurationState as Change<typeof changeName>;
            return (
                <SummaryThreeColumn
                    key={changeName}
                    title={title}
                    valueOne={change.state.value.installed ? "Ja" : "Nein"}
                    valueTwo={
                        change.state.value.installed
                            ? formatedNumber(
                                  change.state.value.demand.amount || 0,
                                  change.state.value.demand.precision,
                              ) +
                              " " +
                              change.state.value.demand.unit
                            : ""
                    }
                    valid={!change.default && !change.state.error}
                    changeName={changeName}
                    navigate={navigate}
                />
            );
        }
        case ChangeNames.NEW_CONNECTION_ELECTRIC_MISCELLANEOUS:
        case ChangeNames.CHARGER_ELECTRIC_MISCELLANEOUS:
        case ChangeNames.ADJUSTABLE_LOAD_ELECTRIC_MISCELLANEOUS:
        case ChangeNames.INCREASE_EXPECTED_ELECTRIC_MISCELLANEOUS: {
            const change = configurationState as Change<typeof changeName>;
            return (
                <SummaryThreeColumn
                    key={changeName}
                    title={title}
                    valueOne={
                        change.state.value.installed
                            ? change.state.value.description || "Ja"
                            : "Nein"
                    }
                    valueTwo={
                        change.state.value.installed
                            ? formatedNumber(
                                  change.state.value.demand.amount || 0,
                                  change.state.value.demand.precision,
                              ) +
                              " " +
                              change.state.value.demand.unit
                            : ""
                    }
                    valid={!change.default && !change.state.error}
                    changeName={changeName}
                    navigate={navigate}
                />
            );
        }
        case ChangeNames.INCREASE_CURRENT_ELECTRIC_MISCELLANEOUS:
            const change = configurationState as Change<typeof changeName>;
            return (
                <SummaryThreeColumn
                    key={changeName}
                    title={title}
                    valueOne={
                        change.state.value.installed
                            ? change.state.value.description || "Ja"
                            : "Nein"
                    }
                    valueTwo={
                        change.state.value.installed
                            ? change.state.value.notKnown
                                ? "nicht bekannt"
                                : formatedNumber(
                                      change.state.value.demand.amount || 0,
                                      change.state.value.demand.precision,
                                  ) +
                                  " " +
                                  change.state.value.demand.unit
                            : ""
                    }
                    valid={!change.default && !change.state.error}
                    changeName={changeName}
                    navigate={navigate}
                />
            );
        case ChangeNames.INCREASE_CURRENT_ELECTRIC_BOILER:
        case ChangeNames.INCREASE_CURRENT_ELECTRIC_ELEVATOR:
        case ChangeNames.INCREASE_CURRENT_ELECTRIC_LOADER:
        case ChangeNames.INCREASE_CURRENT_ELECTRIC_SHARED_ELECTRICITY:
        case ChangeNames.INCREASE_CURRENT_ELECTRIC_SAUNA:
        case ChangeNames.INCREASE_CURRENT_ELECTRIC_HEAT_PUMP: {
            const change = configurationState as Change<typeof changeName>;
            return (
                <SummaryThreeColumn
                    key={changeName}
                    title={title}
                    valueOne={change.state.value.installed ? "Ja" : "Nein"}
                    valueTwo={
                        change.state.value.installed && !change.state.value.notKnown
                            ? formatedNumber(
                                  change.state.value.demand.amount || 0,
                                  change.state.value.demand.precision,
                              ) +
                              " " +
                              change.state.value.demand.unit
                            : change.state.value.installed && change.state.value.notKnown
                            ? "nicht bekannt"
                            : ""
                    }
                    valid={!change.default && !change.state.error}
                    changeName={changeName}
                    navigate={navigate}
                />
            );
        }

        case ChangeNames.NEW_CONNECTION_MULTILINE_DESIRES: {
            const change = configurationState as Change<typeof changeName>;
            return (
                <SummaryTwoColumn
                    key={changeName}
                    title={title}
                    value={
                        namingRule?.rules?.find(
                            (r) =>
                                r.condition.yes === change.state.value.yes ||
                                r.condition.no === change.state.value.no ||
                                r.condition.thirdParty === change.state.value.thirdParty,
                        )?.name || "-"
                    }
                    valid={!change.default && !change.state.error}
                    changeName={changeName}
                    navigate={navigate}
                />
            );
        }
        case ChangeNames.NEW_CONNECTION_MULTILINE_VARIANTS: {
            const change = configurationState as Change<typeof changeName>;

            return (
                <Fragment key={changeName}>
                    <SummaryTwoColumn
                        key={`${changeName}Strom`}
                        title={"Strom"}
                        value={change.state.value.lines.value.power ? "Ja" : "Nein"}
                        valid={!change.default && !change.state.error}
                        changeName={changeName}
                        navigate={navigate}
                    />
                    <SummaryTwoColumn
                        key={`${changeName}Wasser`}
                        title={"Wasser"}
                        value={change.state.value.lines.value.water ? "Ja" : "Nein"}
                        valid={!change.default && !change.state.error}
                        changeName={changeName}
                        navigate={navigate}
                    />
                    <SummaryTwoColumn
                        key={`${changeName}Zisterne`}
                        title={"Zisterne"}
                        value={change.state.value.lines.value.cistern ? "Ja" : "Nein"}
                        valid={!change.default && !change.state.error}
                        changeName={changeName}
                        navigate={navigate}
                    />
                    <SummaryTwoColumn
                        key={`${changeName}Gas`}
                        title={"Gas/Flüssiggas"}
                        value={change.state.value.lines.value.gas ? "Ja" : "Nein"}
                        valid={!change.default && !change.state.error}
                        changeName={changeName}
                        navigate={navigate}
                    />
                    <SummaryTwoColumn
                        key={`${changeName}Telek`}
                        title={"Telekommunikation"}
                        value={change.state.value.lines.value.multimedia ? "Ja" : "Nein"}
                        valid={!change.default && !change.state.error}
                        changeName={changeName}
                        navigate={navigate}
                    />
                    <SummaryTwoColumn
                        key={`${changeName}Warmeanlage`}
                        title={"Wärmeanlage"}
                        value={change.state.value.lines.value.heatPump ? "Ja" : "Nein"}
                        valid={!change.default && !change.state.error}
                        changeName={changeName}
                        navigate={navigate}
                    />
                    <SummaryTwoColumn
                        key={`${changeName}Außenbeleuchtung`}
                        title={"Außenbeleuchtung"}
                        value={change.state.value.lines.value.outdoorLighting ? "Ja" : "Nein"}
                        valid={!change.default && !change.state.error}
                        changeName={changeName}
                        navigate={navigate}
                    />
                    <SummaryTwoColumn
                        key={`${changeName}Others`}
                        title={"Weitere Sparten"}
                        value={change.state.value.additionalLines.value || "Nein"}
                        valid={!change.default && !change.state.error}
                        changeName={changeName}
                        navigate={navigate}
                    />
                    {change.state.value.lines.value.water && (
                        <SummaryTwoColumn
                            key={`${changeName}WDiamter`}
                            title={"Außendurchmesser der Wasserleitung"}
                            value={
                                namingRule?.rules?.find(
                                    (r) =>
                                        r.condition.lessOrEqualDa50 ===
                                            change.state.value.waterPipeDiameters.value
                                                ?.lessOrEqualDa50 ||
                                        r.condition.da63 ===
                                            change.state.value.waterPipeDiameters.value?.da63,
                                )?.name || "-"
                            }
                            valid={!change.default && !change.state.error}
                            changeName={changeName}
                            navigate={navigate}
                        />
                    )}
                </Fragment>
            );
        }
        case ChangeNames.NEW_CONNECTION_MULTILINE_CONTACT_ALLOWED: {
            const change = configurationState as Change<typeof changeName>;
            return (
                <SummaryTwoColumn
                    title={title}
                    value={yesNoValue(change.state.value)}
                    valid={!change.default && !change.state.error}
                    changeName={changeName}
                    navigate={navigate}
                />
            );
        }
        case ChangeNames.NEW_CONNECTION_MULTILINE_BASE_PLATE_HEIGHT: {
            const change = configurationState as Change<typeof changeName>;
            return (
                <SummaryTwoColumn
                    title={title}
                    value={
                        formatedNumber(
                            change.state.value.amount.num || 0,
                            change.state.value.precision,
                        ) +
                        " " +
                        change.state.value.unit
                    }
                    valid={!change.default && !change.state.error}
                    changeName={changeName}
                    navigate={navigate}
                />
            );
        }
        case ChangeNames.NEW_CONNECTION_MULTILINE_INSTALLATION_FRAME_INSTALLED_BY_DISTRIBUTION_SYSTEM_OPERATOR: {
            const change = configurationState as Change<typeof changeName>;
            return (
                <SummaryTwoColumn
                    title={title}
                    value={yesNoValue(change.state.value)}
                    valid={!change.default && !change.state.error}
                    changeName={changeName}
                    navigate={navigate}
                />
            );
        }
        case ChangeNames.NEW_CONNECTION_MULTILINE_CELLAR_OUTER_WALL: {
            const change = configurationState as Change<typeof changeName>;
            return (
                <Fragment key={changeName}>
                    <SummaryTwoColumn
                        key={`${changeName}wandstarke`}
                        title={"Wandstärke (ohne Außendämmung)"}
                        value={
                            formatedNumber(
                                change.state.value.wallThickness.value.amount || 0,
                                change.state.value.wallThickness.value.precision,
                            ) +
                            " " +
                            change.state.value.wallThickness.value.unit
                        }
                        valid={!change.state.value.wallThickness.error}
                        changeName={changeName}
                        navigate={navigate}
                    />
                    <SummaryTwoColumn
                        key={`${changeName}DichtungAußen`}
                        title={"Abdichtung auf der Außenwand"}
                        value={
                            change.state.value.sealing && change.state.value.sealing.value
                                ? "Ja"
                                : "Nein"
                        }
                        valid={change.state.value.sealing && !change.state.value.sealing.error}
                        changeName={changeName}
                        navigate={navigate}
                    />
                    <SummaryTwoColumn
                        key={`${changeName}Wandbeschaffenheit`}
                        title={"Wandbeschaffenheit"}
                        value={
                            namingRule?.rules?.find(
                                (r) =>
                                    r.condition.walled ===
                                        change.state.value.characteristics.value.walled ||
                                    r.condition.doubleWall ===
                                        change.state.value.characteristics.value.doubleWall ||
                                    r.condition.whiteTub ===
                                        change.state.value.characteristics.value.whiteTub,
                            )?.name || "-"
                        }
                        valid={!change.default && !change.state.error}
                        changeName={changeName}
                        navigate={navigate}
                    />
                    {change.state.value.characteristics.value.doubleWall && (
                        <SummaryTwoColumn
                            key={`${changeName}Abdichtebene`}
                            title={"Abdichtebene"}
                            value={
                                namingRule?.rules?.find(
                                    (r) =>
                                        r.condition.concrete ===
                                            (change.state.value.doubleWallSealingLayer.value
                                                ?.concrete || false) ||
                                        r.condition.exteriorFormwork ===
                                            (change.state.value.doubleWallSealingLayer.value
                                                ?.exteriorFormwork || false) ||
                                        r.condition.onOuterWall ===
                                            (change.state.value.doubleWallSealingLayer.value
                                                ?.onOuterWall || false),
                                )?.name || "-"
                            }
                            valid={
                                change.state.value.doubleWallSealingLayer.value != null &&
                                !change.state.value.doubleWallSealingLayer.error
                            }
                            changeName={changeName}
                            navigate={navigate}
                        />
                    )}
                    {change.state.value.characteristics.value.doubleWall && (
                        <SummaryTwoColumn
                            key={`${changeName}innereIsolierung`}
                            title={"Innenliegende Isolierung"}
                            value={
                                change.state.value.doubleWallInteriorInsulation.value == null
                                    ? "-"
                                    : change.state.value.doubleWallInteriorInsulation.value
                                    ? "Ja"
                                    : "Nein"
                            }
                            valid={
                                change.state.value.doubleWallInteriorInsulation.value != null &&
                                !change.state.value.doubleWallInteriorInsulation.error
                            }
                            changeName={changeName}
                            navigate={navigate}
                        />
                    )}
                </Fragment>
            );
        }
        case ChangeNames.NEW_CONNECTION_MULTILINE_CELLAR_OUTER_WALL_BUSHING: {
            const change = configurationState as Change<typeof changeName>;

            return (
                <SummaryTwoColumn
                    title={title}
                    value={
                        (change.state.value.coreDrilling && "Kernbohrung") ||
                        (change.state.value.casingPipe && "Futterrohr") ||
                        "-"
                    }
                    valid={true}
                    changeName={changeName}
                    navigate={navigate}
                />
            );
        }
        case ChangeNames.ADDITIONAL_NOTES: {
            const change = configurationState as Change<typeof changeName>;
            return (
                <SummaryTwoColumn
                    key={"additionalNotes"}
                    title={"Weitere Information"}
                    value={change.state.value.text || "-"}
                    valid={true}
                    changeName={changeName}
                    navigate={navigate}
                />
            );
        }

        case ChangeNames.DATA_PROTECTION_AGREEMENT: {
            const change = configurationState as Change<typeof changeName>;
            return (
                <SummaryTwoColumn
                    key={changeName}
                    title={"Zustimmung zum Datenschutz"}
                    value={yesNoValue(change.state.value)}
                    valid={true}
                    changeName={changeName}
                    navigate={navigate}
                />
            );
        }

        case ChangeNames.TERMS_OF_USE_AGREEMENT: {
            const change = configurationState as Change<typeof changeName>;
            return (
                <SummaryTwoColumn
                    key={changeName}
                    title={"Zustimmung zu den Nutzungsbedingungen"}
                    value={yesNoValue(change.state.value)}
                    valid={true}
                    changeName={changeName}
                    navigate={navigate}
                />
            );
        }

        case ChangeNames.ADJUSTABLE_LOAD_AGREEMENT: {
            const change = configurationState as Change<typeof changeName>;
            return (
                <SummaryTwoColumn
                    key={changeName}
                    title={"Zustimmung zu §14a"}
                    value={yesNoValue(change.state.value)}
                    valid={true}
                    changeName={changeName}
                    navigate={navigate}
                />
            );
        }

        case ChangeNames.NEW_CONNECTION_MULTILINE_CELLAR_CASING_PIPE_DELIVERED_BY_DISTRIBUTION_SYSTEM_OPERATOR: {
            const change = configurationState as Change<typeof changeName>;
            return (
                <SummaryTwoColumn
                    key={changeName}
                    title={title}
                    value={yesNoValue(
                        change.state.value,
                        "Lieferung durch Syna",
                        "Eigenes Futterrohr",
                    )}
                    valid={!change.default && !change.state.error}
                    changeName={changeName}
                    navigate={navigate}
                />
            );
        }
        case ChangeNames.NEW_CONNECTION_MULTILINE_CELLAR_INSTALLATION_FRAME_INSTALLED_BY_DISTRIBUTION_SYSTEM_OPERATOR: {
            const change = configurationState as Change<typeof changeName>;
            return (
                <SummaryTwoColumn
                    key={changeName}
                    title={title}
                    value={yesNoValue(change.state.value)}
                    valid={!change.default && !change.state.error}
                    changeName={changeName}
                    navigate={navigate}
                />
            );
        }

        case ChangeNames.MODIFICATION_POWER_FEEDS: {
            const change = configurationState as Change<typeof changeName>;
            return (
                <SummaryTwoColumn
                    title={title}
                    value={
                        namingRule?.rules?.find(
                            (r) =>
                                r.condition.cable === change.state.value.cable ||
                                r.condition.overhead === change.state.value.overhead,
                        )?.name || "-"
                    }
                    valid={!change.default && !change.state.error}
                    changeName={changeName}
                    navigate={navigate}
                />
            );
        }
        case ChangeNames.MODIFICATION_ELECTRIC_BUSINESS_DEMAND: {
            const change = configurationState as Change<typeof changeName>;
            return (
                <SummaryThreeColumn
                    key={changeName}
                    title={title}
                    valueOne={change.state.value.installed ? "Ja" : "Nein"}
                    valueTwo={
                        change.state.value.installed
                            ? formatedNumber(
                                  change.state.value.demand.amount || 0,
                                  change.state.value.demand.precision,
                              ) +
                              " " +
                              change.state.value.demand.unit
                            : ""
                    }
                    valid={!change.default && !change.state.error}
                    changeName={changeName}
                    navigate={navigate}
                />
            );
        }
        case ChangeNames.MODIFICATION_POWER_CABLE: {
            const change = configurationState as Change<typeof changeName>;
            return (
                <SummaryTwoColumn
                    title={title}
                    value={
                        namingRule?.rules?.find(
                            (r) =>
                                r.condition.junctionBoxRelocation ===
                                    change.state.value.junctionBoxRelocation ||
                                r.condition.junctionBoxAndCableRelocation ===
                                    change.state.value.junctionBoxAndCableRelocation ||
                                r.condition.cableRelocation ===
                                    change.state.value.cableRelocation ||
                                r.condition.junctionBoxReplacement ===
                                    change.state.value.junctionBoxReplacement,
                        )?.name || "-"
                    }
                    valid={!change.default && !change.state.error}
                    changeName={changeName}
                    navigate={navigate}
                />
            );
        }

        case ChangeNames.MODIFICATION_POWER_OVERHEAD: {
            const change = configurationState as Change<typeof changeName>;
            return (
                <SummaryTwoColumn
                    title={title}
                    value={
                        namingRule?.rules?.find(
                            (r) =>
                                r.condition.junctionBoxReplacement ===
                                    change.state.value.junctionBoxReplacement ||
                                r.condition.roofPillarReassembly ===
                                    change.state.value.roofPillarReassembly ||
                                r.condition.roofPillarElevation ===
                                    change.state.value.roofPillarElevation,
                        )?.name || "-"
                    }
                    valid={!change.default && !change.state.error}
                    changeName={changeName}
                    navigate={navigate}
                />
            );
        }
        case ChangeNames.MODIFICATION_POWER_OVERHEAD_ROOF_PILLAR_REASSEMBLY_LOCATION: {
            const change = configurationState as Change<typeof changeName>;
            return (
                <SummaryTwoColumn
                    title={title}
                    value={
                        namingRule?.rules?.find(
                            (r) =>
                                r.condition.same === change.state.value.same ||
                                r.condition.other === change.state.value.other,
                        )?.name || "-"
                    }
                    valid={!change.default && !change.state.error}
                    changeName={changeName}
                    navigate={navigate}
                />
            );
        }
        case ChangeNames.MODIFICATION_POWER_JUNCTION_BOX_REPLACEMENT_REASON: {
            const change = configurationState as Change<typeof changeName>;
            return (
                <SummaryTwoColumn
                    title={title}
                    value={
                        namingRule?.rules?.find(
                            (r) =>
                                r.condition.defect === change.state.value.defect ||
                                r.condition.other === change.state.value.other,
                        )?.name || "-"
                    }
                    valid={!change.default && !change.state.error}
                    changeName={changeName}
                    navigate={navigate}
                />
            );
        }
        case ChangeNames.MODIFICATION_GAS_RELOCATION_DISTANCE: {
            const change = configurationState as Change<typeof changeName>;
            return (
                <SummaryTwoColumn
                    key={changeName}
                    title={title}
                    value={
                        formatedNumber(
                            change.state.value.amount.num || 0,
                            change.state.value.precision,
                        ) +
                        " " +
                        change.state.value.unit
                    }
                    valid={!change.default && !change.state.error}
                    changeName={changeName}
                    navigate={navigate}
                />
            );
        }
        case ChangeNames.MODIFICATION_REASON: {
            const change = configurationState as Change<typeof changeName>;
            return (
                <SummaryTwoColumn
                    key={changeName}
                    title={title}
                    value={change.state.value.text || ""}
                    valid={change.state.value != null && !change.state.error}
                    changeName={changeName}
                    navigate={navigate}
                />
            );
        }
        case ChangeNames.NEW_CONNECTION_MULTILINE_PERSONAL_CONTRIBUTION_CAN_DO_UNDERGROUND_WORK:
        case ChangeNames.NEW_CONNECTION_PERSONAL_CONTRIBUTION_CAN_DO_UNDERGROUND_WORK: {
            const change = configurationState as Change<typeof changeName>;
            return (
                <SummaryTwoColumn
                    key={changeName}
                    title="Tiefbau"
                    value={change.state.value.yes ? "Ja" : "Nein"}
                    valid={change.state.value != null && !change.state.error}
                    changeName={changeName}
                    navigate={navigate}
                />
            );
        }

        case ChangeNames.NEW_CONNECTION_MULTILINE_PERSONAL_CONTRIBUTION_CAN_DO_WALL_BUSHING: {
            const change = configurationState as Change<typeof changeName>;
            return (
                <SummaryTwoColumn
                    key={changeName}
                    title="Mauerdurchbruch/Kernbohrung/Futterrohr"
                    value={change.state.value.yes ? "Ja" : "Nein"}
                    valid={change.state.value != null && !change.state.error}
                    changeName={changeName}
                    navigate={navigate}
                />
            );
        }

        case ChangeNames.NEW_CONNECTION_PERSONAL_CONTRIBUTION_CAN_DO_WALL_BUSHING: {
            const change = configurationState as Change<typeof changeName>;
            return (
                <SummaryTwoColumn
                    key={changeName}
                    title="Mauerdurchbruch/Kernbohrung"
                    value={change.state.value.yes ? "Ja" : "Nein"}
                    valid={change.state.value != null && !change.state.error}
                    changeName={changeName}
                    navigate={navigate}
                />
            );
        }

        case ChangeNames.NEW_CONNECTION_PERSONAL_CONTRIBUTION_CAN_DO_BASE_PLATE_BUSHING: {
            const change = configurationState as Change<typeof changeName>;
            return (
                <SummaryTwoColumn
                    key={changeName}
                    title="Bodenplattendurchdringung"
                    value={change.state.value.yes ? "Ja" : "Nein"}
                    valid={change.state.value != null && !change.state.error}
                    changeName={changeName}
                    navigate={navigate}
                />
            );
        }

        case ChangeNames.NEW_CONNECTION_PERSONAL_CONTRIBUTION_MUST_DO_BASE_PLATE_BUSHING: {
            const change = configurationState as Change<typeof changeName>;
            return (
                <SummaryTwoColumn
                    key={changeName}
                    title="Bodenplattendurchdringung"
                    value={change.state.value.yes ? "Akzeptiert" : "Nicht akzeptiert"}
                    valid={change.state.value != null && !change.state.error}
                    changeName={changeName}
                    navigate={navigate}
                />
            );
        }

        case ChangeNames.NEW_CONNECTION_MULTILINE_PERSONAL_CONTRIBUTION_MUST_DO_UNDERGROUND_WORK: {
            const change = configurationState as Change<typeof changeName>;
            return (
                <SummaryTwoColumn
                    key={changeName}
                    title="Tiefbau"
                    value={change.state.value.yes ? "Akzeptiert" : "Nicht akzeptiert"}
                    valid={change.state.value.yes && !change.state.error}
                    changeName={changeName}
                    navigate={navigate}
                />
            );
        }

        case ChangeNames.NEW_CONNECTION_MULTILINE_BUILDING_INTAKE_SPECIFICATION: {
            const change = configurationState as Change<typeof changeName>;
            return (
                <SummaryTwoColumn
                    key={changeName}
                    title="Bauwerksdurchdringung"
                    value={change.state.value.text || ""}
                    valid={!change.state.error}
                    changeName={changeName}
                    navigate={navigate}
                />
            );
        }

        case ChangeNames.NEW_CONNECTION_MULTILINE_PERSONAL_CONTRIBUTION_MUST_DO_WALL_BUSHING: {
            const change = configurationState as Change<typeof changeName>;
            return (
                <SummaryTwoColumn
                    key={changeName}
                    title="Mauerdurchbruch/Kernbohrung/Futterrohr"
                    value={change.state.value.yes ? "Akzeptiert" : "Nicht akzeptiert"}
                    valid={change.state.value.yes && !change.state.error}
                    changeName={changeName}
                    navigate={navigate}
                />
            );
        }
        case ChangeNames.NEW_CONNECTION_MULTILINE_PERSONAL_CONTRIBUTION_MUST_INFUSE_OWN_INSTALLATION_FRAME:
        case ChangeNames.NEW_CONNECTION_MULTILINE_PERSONAL_CONTRIBUTION_MUST_INFUSE_PROVIDED_INSTALLATION_FRAME: {
            const change = configurationState as Change<typeof changeName>;
            return (
                <SummaryTwoColumn
                    key={changeName}
                    title="Rohbauteil eingießen"
                    value={change.state.value.yes ? "Akzeptiert" : "Nicht akzeptiert"}
                    valid={change.state.value.yes && !change.state.error}
                    changeName={changeName}
                    navigate={navigate}
                />
            );
        }

        case ChangeNames.NEW_CONNECTION_MULTILINE_PERSONAL_CONTRIBUTION_MUST_INSTALL_CASING_PIPE: {
            const change = configurationState as Change<typeof changeName>;
            return (
                <SummaryTwoColumn
                    key={changeName}
                    title="Futterrohr"
                    value={change.state.value.yes ? "Akzeptiert" : "Nicht akzeptiert"}
                    valid={change.state.value.yes && !change.state.error}
                    changeName={changeName}
                    navigate={navigate}
                />
            );
        }
        case ChangeNames.REMOVAL_DATE: {
            const change = configurationState as Change<typeof changeName>;
            const from = change.state.value?.from;
            return (
                <SummaryTwoColumn
                    key={changeName}
                    title="Demontage Zeitraum"
                    value={`${from ? KWFormatter(from) : ""}`}
                    valid={!!change.state.value && !change.state.error}
                    changeName={changeName}
                    navigate={navigate}
                />
            );
        }
        case ChangeNames.REMOVAL_RECONSTRUCTION_DATE: {
            const change = configurationState as Change<typeof changeName>;
            const fromSecs = change.state.value?.from;
            const from = moment.unix(fromSecs || 0);
            return (
                <SummaryTwoColumn
                    key={changeName}
                    title="Wiederbebauung"
                    value={fromSecs ? `${MONTHS[from.month()]} ${from.year()}` : ""}
                    valid={!!change.state.value && !change.state.error}
                    changeName={changeName}
                    navigate={navigate}
                />
            );
        }
        case ChangeNames.CHARGER_LOCATION: {
            const change = configurationState as Change<typeof changeName>;
            return (
                <SummaryTwoColumn
                    key={changeName}
                    title={title}
                    value={
                        change.state.value.publicParkingGround
                            ? "auf öffentlich zugänglichem Grund"
                            : change.state.value.privateParkingGround
                            ? "auf privatem Grund"
                            : "-"
                    }
                    valid={!!change.state.value && !change.state.error}
                    changeName={changeName}
                    navigate={navigate}
                />
            );
        }
        case ChangeNames.CHARGER_PUBLIC_PARKING_USAGES: {
            const change = configurationState as Change<typeof changeName>;
            return (
                <SummaryTwoColumn
                    key={changeName}
                    title={title}
                    value={
                        change.state.value.allDay
                            ? "rund um die Uhr"
                            : change.state.value.whenOpen
                            ? "zu den Öffnungszeiten"
                            : "-"
                    }
                    valid={!!change.state.value && !change.state.error}
                    changeName={changeName}
                    navigate={navigate}
                />
            );
        }
        case ChangeNames.CHARGER_PRIVATE_PARKING_USAGES: {
            const change = configurationState as Change<typeof changeName>;
            return (
                <SummaryTwoColumn
                    key={changeName}
                    title={title}
                    value={
                        change.state.value.private
                            ? "auf dem Privatparkplatz"
                            : change.state.value.employee
                            ? "auf dem Mitarbeiterparkplatz"
                            : "-"
                    }
                    valid={!!change.state.value && !change.state.error}
                    changeName={changeName}
                    navigate={navigate}
                />
            );
        }
        case ChangeNames.CHARGER_SUPPLY_CONNECTION: {
            const change = configurationState as Change<typeof changeName>;
            return (
                <SummaryTwoColumn
                    key={changeName}
                    title="Anschluss der Ladeeinrichtung"
                    value={
                        change.state.value.existing
                            ? "Bestehender Netzanschluss"
                            : change.state.value.new
                            ? "Neuer/separater Anschluss"
                            : "-"
                    }
                    valid={!!change.state.value && !change.state.error}
                    changeName={changeName}
                    navigate={navigate}
                />
            );
        }
        case ChangeNames.CHARGER_ADDITIONAL_ELECTRIC_DEMAND: {
            const change = configurationState as Change<typeof changeName>;
            return (
                <SummaryThreeColumn
                    key={changeName}
                    title="Zusätzlich benötigte Leistung"
                    valueOne={change.state.value.installed ? "Ja" : "Nein"}
                    valueTwo={
                        change.state.value.installed
                            ? formatedNumber(
                                  change.state.value.demand.amount || 0,
                                  change.state.value.demand.precision,
                              ) +
                              " " +
                              change.state.value.demand.unit
                            : ""
                    }
                    valid={!change.default && !change.state.error}
                    changeName={changeName}
                    navigate={navigate}
                />
            );
        }
        case ChangeNames.CHARGER_PERSONAL_CONTRIBUTION_CAN_DO_UNDERGROUND_WORK: {
            const change = configurationState as Change<typeof changeName>;
            return (
                <SummaryTwoColumn
                    key={changeName}
                    title="Tiefbau"
                    value={change.state.value.yes ? "Ja" : "Nein"}
                    valid={change.state.value != null && !change.state.error}
                    changeName={changeName}
                    navigate={navigate}
                />
            );
        }
        case ChangeNames.CHARGER_HOUSE_CONNECTION_FUSE_CAPACITY:
        case ChangeNames.ADJUSTABLE_LOAD_HOUSE_CONNECTION_FUSE_CAPACITY: {
            const change = configurationState as Change<typeof changeName>;
            return (
                <SummaryThreeColumn
                    key={changeName}
                    title="Hausanschlusssicherung bekannt"
                    valueOne={change.state.value.installed ? "Ja" : "Nein"}
                    valueTwo={
                        change.state.value.installed
                            ? formatedNumber(
                                  change.state.value.current.amount || 0,
                                  change.state.value.current.precision,
                              ) +
                              " " +
                              change.state.value.current.unit
                            : ""
                    }
                    valid={!change.default && !change.state.error}
                    changeName={changeName}
                    navigate={navigate}
                />
            );
        }
        case ChangeNames.INCREASE_CURRENT_HOUSE_CONNECTION_FUSE_CAPACITY: {
            const change = configurationState as Change<typeof changeName>;
            return (
                <SummaryThreeColumn
                    key={changeName}
                    title="Hausanschlusssicherung bekannt?"
                    valueOne={change.state.value.installed ? "Ja" : "Nein"}
                    valueTwo={
                        change.state.value.installed
                            ? formatedNumber(
                                  change.state.value.current.amount || 0,
                                  change.state.value.current.precision,
                              ) +
                              " " +
                              change.state.value.current.unit
                            : ""
                    }
                    valid={!change.default && !change.state.error}
                    changeName={changeName}
                    navigate={navigate}
                />
            );
        }

        case ChangeNames.CHARGER_CHARGING_STATIONS: {
            const change = configurationState as Change<typeof changeName>;
            const formatter = new Intl.NumberFormat("de-DE");
            return (
                <Fragment>
                    {change.state.value.map((chargingStation, idx) => {
                        return (
                            <SummaryTwoColumn
                                key={`${changeName}-${idx}`}
                                title={`${chargingStation.count} x Ladeeinrichtung`}
                                value={`je ${
                                    chargingStation.chargingPoints.length
                                } Ladepunkt(e) (${chargingStation.chargingPoints
                                    .map((y) => formatter.format(y.value.amount || 0))
                                    .join("; ")} ${chargingStation.chargingPoints[0].value.unit})`}
                                valid={
                                    !change.default &&
                                    chargingStation.chargingPoints.every((x) => !x.error)
                                }
                                changeName={changeName}
                                navigate={navigate}
                            />
                        );
                    })}
                    <SummaryTwoColumn
                        key={changeName}
                        title={`Gesamtleistung aller Ladeeinrichtungen`}
                        value={`${formatter.format(
                            sumUpChargingStationPower(change.state.value),
                        )} ${change.state.value[0].chargingPoints[0].value.unit}`}
                        valid={!change.default && !change.state.error}
                        changeName={changeName}
                        navigate={navigate}
                    />
                </Fragment>
            );
        }

        case ChangeNames.CHARGER_LOAD_MANAGEMENT: {
            const change = configurationState as Change<typeof changeName>;
            return (
                <SummaryTwoColumn
                    key={changeName}
                    title="Lastmanagementsystem"
                    value={
                        change.state.value.loadManagement.static
                            ? `statisch (max. begrenzte Leistung ${change.state.value.demand.amount} kVA`
                            : change.state.value.loadManagement.dynamic
                            ? `dynamisch (max. mögliche Leistung ${change.state.value.demand.amount} kVA`
                            : "nicht vorhanden"
                    }
                    valid={!!change.state.value && !change.state.error}
                    changeName={changeName}
                    navigate={navigate}
                />
            );
        }

        case ChangeNames.ADJUSTABLE_LOAD_ELECTRIC_INSTALLATION_AGREEMENT:
        case ChangeNames.CHARGER_ELECTRIC_INSTALLATION_AGREEMENT: {
            const change = configurationState as Change<typeof changeName>;
            return (
                <SummaryTwoColumn
                    key={changeName}
                    title={"Technische Anforderungen"}
                    value={change.state.value.yes ? "Akzeptiert" : "Nicht akzeptiert"}
                    valid={!change.state.error}
                    changeName={changeName}
                    navigate={navigate}
                />
            );
        }
        case ChangeNames.INCREASE_ADDITIONAL_NOTES: {
            const change = configurationState as Change<typeof changeName>;
            return (
                <SummaryTwoColumn
                    key={"additionalNotes"}
                    title={"Begründung"}
                    value={change.state.value.text || "-"}
                    valid={true}
                    changeName={changeName}
                    navigate={navigate}
                />
            );
        }
        case ChangeNames.APPLICANT:
        case ChangeNames.INVOICE_RECIPIENT:
        case ChangeNames.PROPERTY_OWNER:
        case ChangeNames.BUILDING_ADDRESS:
        case ChangeNames.SYSTEM_CONSTRUCTOR: {
            return <Fragment />;
        }
    }
    exhaustiveCheck(changeName); // <- provides better error message than "missing final return statement.."
};

const SummaryGroup: FC<PropsSummaryGroup> = ({
    configuration,
    group,
    navigate,
}: PropsSummaryGroup) => {
    const allowedSummaryNames = group.summaries?.map((item) => item.summary);

    const filteredConfigurationStateKeys = (
        allowedSummaryNames as Array<keyof typeof configuration.state>
    ).filter((key) => configuration.state[key] && configuration.state[key]?.relevant);

    if (filteredConfigurationStateKeys === undefined || filteredConfigurationStateKeys.length === 0)
        return <Fragment />;

    return (
        <Group title={group.title} key={group.title}>
            {filteredConfigurationStateKeys.map((changeName) =>
                twoOrThreeColumn(
                    group.summaries?.find((s) => s.summary === changeName)?.title || "",
                    changeName,
                    configuration,
                    navigate,
                ),
            )}
        </Group>
    );
};

const AddressLine: FC<PropsAddressLine> = ({ label, value, valid, navigate }: PropsAddressLine) => {
    const classes = useStyles();

    const classLabel = [classes.addressLabel, valid ? null : classes.addressError].join(" ");
    const classValue = [classes.addressValue, valid ? null : classes.addressError].join(" ");
    return (
        <div
            className={classes.addressContainer}
            onClick={navigate && (() => navigate("addresses"))}
        >
            <div className={classLabel}>{label}</div>
            <div className={classValue}>{value}</div>
        </div>
    );
};

const Address: FC<PropsAddress> = ({ configuration, group, navigate }: PropsAddress) => {
    const rows: JSX.Element[] = [];

    const formatedAddressline = (
        s: string | null,
        h: string | null,
        z: string | null,
        c: string | null,
    ): string => {
        const sh = s && h ? s + " " + h : s || h; // Strasse und Hausnummer mit Blank dazwischen wenn beide gegeben
        const zc = z && c ? z + " " + c : z || c; // Plz und Ort mit Blank dazwischen, wenn beide gegeben
        return (sh && zc ? sh + ", " + zc : sh || zc) + ""; // Strasse Hausnummer "komma Blank" plz ort, wenn nötig
    };

    const formatedMarkline = (m: string | null, c: string | null, p: string | null): string => {
        return m && c ? m + ", " + c + ", " + p : m + ", " + p;
    };

    const formatedNameline = (f: string | null, l: string | null = ""): string => {
        return (f && l ? f + " " + l : f || l) + ""; // Vorname und Nachname, dann Blank dazwischen, sonst der jeweilige Wert
    };

    let key = 0;

    function addNaturalAddress(natural: NaturalPerson): void {
        rows.push(
            <AddressLine
                label="Name"
                value={formatedNameline(natural.firstName.value, natural.lastName.value)}
                valid={!natural.firstName.error && !natural.lastName.error}
                key={key++}
                navigate={navigate}
            />,
        );
        rows.push(
            <AddressLine
                label="Adresse"
                value={formatedAddressline(
                    natural.address.street.value,
                    natural.address.houseNumber.value,
                    natural.address.zipCode.value,
                    natural.address.city.value,
                )}
                valid={
                    !natural.address.street.error &&
                    !natural.address.houseNumber.error &&
                    !natural.address.zipCode.error &&
                    !natural.address.city.error
                }
                key={key++}
                navigate={navigate}
            />,
        );
        rows.push(
            <AddressLine
                label="Email"
                value={natural.email.value || ""}
                valid={!natural.email.error}
                key={key++}
                navigate={navigate}
            />,
        );
        rows.push(
            <AddressLine
                label="Telefon"
                value={natural.phoneNumber.value || ""}
                valid={!natural.phoneNumber.error}
                key={key++}
                navigate={navigate}
            />,
        );
    }

    function addLegalAddress(legal: LegalPerson): void {
        rows.push(
            <AddressLine
                label="Name"
                value={formatedNameline(legal.name.value)}
                valid={!legal.name.error}
                key={key++}
                navigate={navigate}
            />,
        );
        rows.push(
            <AddressLine
                label="Adresse"
                value={formatedAddressline(
                    legal.address.street.value,
                    legal.address.houseNumber.value,
                    legal.address.zipCode.value,
                    legal.address.city.value,
                )}
                valid={
                    !legal.address.street.error &&
                    !legal.address.houseNumber.error &&
                    !legal.address.zipCode.error &&
                    !legal.address.city.error
                }
                key={key++}
                navigate={navigate}
            />,
        );
        rows.push(
            <AddressLine
                label="Email"
                value={legal.email.value || ""}
                valid={!legal.email.error}
                key={key++}
                navigate={navigate}
            />,
        );
        rows.push(
            <AddressLine
                label="Telefon"
                value={legal.phoneNumber.value || ""}
                valid={!legal.phoneNumber.error}
                key={key++}
                navigate={navigate}
            />,
        );
    }

    if (group.summary?.type === "buildingAddress") {
        const state = configuration.state.buildingAddress?.state;
        if (!state) return <Fragment />;

        const address = formatedAddressline(
            state.street?.value || "",
            state.houseNumber?.value || "",

            state.zipCode.value,
            state.city.value,
        );

        let markLine = "";
        if (state.type === typeCadaster) {
            markLine = formatedMarkline(
                state.mark.value,
                state.corridor?.value || "",
                state.parcel.value,
            );
        }

        const valid =
            !state.street?.error &&
            !state.houseNumber?.error &&
            !state.zipCode.error &&
            !state.city.error;
        rows.push(<AddressLine label="Adresse" value={address} valid={valid} key={key++} />);
        if (markLine) {
            rows.push(
                <AddressLine label="Katasterangaben" value={markLine} valid={valid} key={key++} />,
            );
        }
    }

    if (group.summary?.type === "naturalLegal") {
        const changeName = group.summary.summary;
        const configurationState =
            configuration.state[changeName as keyof typeof configuration.state];

        if (!configurationState) {
            return <Fragment />;
        }

        if (changeName === ChangeNames.INVOICE_RECIPIENT) {
            const change = configurationState as Change<ChangeNames.INVOICE_RECIPIENT>;
            if (change.state.value.natural) {
                addNaturalAddress(change.state.value.natural);
            } else if (change.state.value.legal) {
                addLegalAddress(change.state.value.legal);
            }
        }

        if (changeName === ChangeNames.SYSTEM_CONSTRUCTOR) {
            const change = configurationState as Change<ChangeNames.SYSTEM_CONSTRUCTOR>;
            if (!change.relevant) {
                return <Fragment />;
            }

            addLegalAddress(change.state.value.legal);
        }

        if (changeName === ChangeNames.PROPERTY_OWNER) {
            const change = configurationState as Change<ChangeNames.PROPERTY_OWNER>;

            if (change.state.value.equalToInvoiceRecipient) return <Fragment />;

            if (change.state.value.partner.natural) {
                addNaturalAddress(change.state.value.partner.natural);
            } else if (change.state.value.partner.legal) {
                addLegalAddress(change.state.value.partner.legal);
            }
        }

        if (changeName === ChangeNames.APPLICANT) {
            const change = configurationState as Change<ChangeNames.APPLICANT>;
            if (change.state.value.equalToInvoiceRecipient) return <Fragment />;

            if (change.state.value.partner.natural) {
                addNaturalAddress(change.state.value.partner.natural);
            } else if (change.state.value.partner.legal) {
                addLegalAddress(change.state.value.partner.legal);
            }
        }
    }
    return (
        <Group title={group.title}>
            <Fragment>{rows}</Fragment>
        </Group>
    );
};

const Sections: FC<PropsSections> = ({ configuration, navigate }: PropsSections) => {
    const classes = useStyles();
    return (
        <Fragment>
            {ReportDefinition.sections
                .filter((section) => {
                    return section.isVisible ? section.isVisible(configuration) : true;
                })
                .map((section) => {
                    return (
                        <Section title={section.title} key={section.title}>
                            {section.type === "value" && section.groups !== undefined && (
                                <Fragment>
                                    {section.groups.map((group) => (
                                        <SummaryGroup
                                            configuration={configuration}
                                            group={group}
                                            key={group.title}
                                            navigate={navigate}
                                        />
                                    ))}
                                </Fragment>
                            )}
                            {section.type === "document" && (
                                <Documents configuration={configuration} navigate={navigate} />
                            )}
                            {section.type === "address" && section.groups !== undefined && (
                                <div className={classes.groupContainer}>
                                    {section.groups.map((group) => (
                                        <Address
                                            configuration={configuration}
                                            group={group}
                                            key={group.title}
                                            navigate={navigate}
                                        />
                                    ))}
                                </div>
                            )}
                        </Section>
                    );
                })}
        </Fragment>
    );
};

const AdditionalNotesWrapper = ({ configuration, navigate }: PropsSections): React.ReactElement => {
    const classes = useStyles();
    const { values, setValues } = useFormModel<ChangeNames.ADDITIONAL_NOTES>();
    return (
        <Fragment>
            <Sections configuration={configuration} navigate={navigate} />
            <div className={classes.additionalInfoTitle}>Weitere Informationen</div>
            <LabledMultilineTextField
                placeholder="Hier können Sie uns gerne weitere Informationen zu Ihrem Antrag mitteilen."
                margin="none"
                inputProps={{
                    maxLength: 1000,
                }}
                value={values.additionalNotes.value.text || ""}
                onChange={(event) => {
                    setValues((values) => {
                        values.additionalNotes.value.text = event.target.value || "";
                    });
                }}
                error={values.additionalNotes.error !== null}
            />
        </Fragment>
    );
};

const AdjustableLoadAgreementWrapper = ({
    configuration,
}: {
    configuration: Configuration;
}): React.ReactElement => {
    const { values, setValues, submitChange } =
        useFormModel<ChangeNames.ADJUSTABLE_LOAD_AGREEMENT>();

    return (
        <Fragment>
            {configuration.state.adjustableLoadAgreement?.relevant &&
                values.adjustableLoadAgreement && (
                    <FormControlLabel
                        isCheckBox={true}
                        style={{ marginTop: 32 }}
                        label={
                            <span>
                                Bei der Anmeldung einer Verbrauchseinrichtung, z.B. Ladeeinrichtung,
                                Wärmepumpe, Gerät zur Raumkühlung, Bezugsspeicher, gelten weitere
                                Voraussetzungen. Diese habe ich zur Kenntnis genommen und für meine
                                Antragstellung beachtet. Sofern meine Verbrauchseinrichtung unter
                                diese Voraussetzungen fällt, stimme ich den hier genannten{" "}
                                <a
                                    target="_blank"
                                    rel="noreferrer"
                                    href="https://www.syna.de/corp/bedingungen-steuerbare-verbrauchseinrichtungen"
                                >
                                    Bedingungen
                                </a>{" "}
                                zu.
                            </span>
                        }
                        sublabel="(Hier können Sie <a target='_blank' rel='noreferrer' href='https://www.syna.de/corp/steuerbare-verbrauchseinrichtungen'>weitere Informationen</a> einsehen.)"
                        control={
                            <Checkbox
                                value="ja"
                                checked={values.adjustableLoadAgreement.value.yes}
                                style={{ alignSelf: "baseline" }}
                                onChange={(event, checked) => {
                                    const newValues = setValues((values) => {
                                        values.adjustableLoadAgreement.value = {
                                            yes: checked,
                                            no: !checked,
                                        };
                                    });
                                    submitChange(newValues);
                                }}
                            />
                        }
                        error={values.adjustableLoadAgreement.error !== null}
                    />
                )}
        </Fragment>
    );
};

const DataProtectionAgreementWrapper = (): React.ReactElement => {
    const { values, setValues, submitChange } =
        useFormModel<ChangeNames.DATA_PROTECTION_AGREEMENT>();

    return (
        <Fragment>
            {values.dataProtectionAgreement && (
                <FormControlLabel
                    isCheckBox={true}
                    style={{ marginTop: 32 }}
                    label="Die Datenschutzerklärung habe ich akzeptiert und zur Kenntnis genommen."
                    sublabel="(Hier können Sie die <a target='_blank' href='https://www.syna.de/corp/datenschutz'>Datenschutzerklärung</a> einsehen.)"
                    control={
                        <Checkbox
                            value="ja"
                            checked={values.dataProtectionAgreement.value.yes}
                            onChange={(event, checked) => {
                                const newValues = setValues((values) => {
                                    values.dataProtectionAgreement.value = {
                                        yes: checked,
                                        no: !checked,
                                    };
                                });
                                submitChange(newValues);
                            }}
                        />
                    }
                    error={values.dataProtectionAgreement.error !== null}
                />
            )}
        </Fragment>
    );
};

const TermsOfUseAgreementWrapper = (): React.ReactElement => {
    const { values, setValues, submitChange } = useFormModel<ChangeNames.TERMS_OF_USE_AGREEMENT>();
    return (
        <Fragment>
            {values.termsOfUseAgreement && (
                <FormControlLabel
                    isCheckBox={true}
                    style={{ marginTop: 32 }}
                    label="Ich stimme den Nutzungsbedingungen zu."
                    sublabel={`(Hier können Sie die <a target='_blank' href='${process.env.PUBLIC_URL}/Nutzungsbedingungen_Hausanschluss_2020_06.pdf'>Nutzungsbedingungen</a> einsehen.)`}
                    control={
                        <Fragment>
                            <Checkbox
                                value="ja"
                                checked={values.termsOfUseAgreement.value.yes}
                                onChange={(event, checked) => {
                                    const newValues = setValues((values) => {
                                        values.termsOfUseAgreement.value = {
                                            yes: checked,
                                            no: !checked,
                                        };
                                    });
                                    submitChange(newValues);
                                }}
                            />
                        </Fragment>
                    }
                    error={values.termsOfUseAgreement.error !== null}
                />
            )}
        </Fragment>
    );
};

export const Summary: FC<Props> = ({ configuration, options }: Props) => {
    const componentClasses = useComponentStyles();

    const summaryPath = useTypedSelector((state) => {
        const order = state.orders.find((order) => order.configurationId === configuration.id);
        const documentPath = order?.summary?.objectId;
        return documentPath;
    });

    const downloadSummary = useCallback(async () => {
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        window.location.href = await defaultFirebaseClient.getDownloadUrl(summaryPath!);
    }, [summaryPath]);

    if (configuration.lockedAt) {
        return (
            <Fragment>
                <Sections configuration={configuration} navigate={options?.navigate} />
                {summaryPath && (
                    <Fragment>
                        <Container
                            style={{ display: "flex", margin: 16, justifyContent: "flex-end" }}
                        >
                            <span className={componentClasses.downloadLink}>
                                <IconButton
                                    style={{ marginLeft: -12 }}
                                    data-testid="download-button"
                                    onClick={downloadSummary}
                                >
                                    <img
                                        alt=""
                                        className={componentClasses.icon}
                                        src={DownloadSvgIcon}
                                    />
                                </IconButton>
                                <a
                                    href="#/"
                                    style={{ cursor: "pointer" }}
                                    onClick={downloadSummary}
                                >
                                    Zusammenfassung herunterladen
                                </a>
                            </span>
                        </Container>
                    </Fragment>
                )}
            </Fragment>
        );
    } else {
        return (
            <Fragment>
                <AdditionalNotesWrapper
                    configuration={configuration}
                    navigate={options?.navigate}
                />
                <DataProtectionAgreementWrapper />
                <TermsOfUseAgreementWrapper />
                <AdjustableLoadAgreementWrapper configuration={configuration} />
            </Fragment>
        );
    }
};
